import { ClientType } from "@multiweb/clients/ClientType";
import { IContextClient } from "@multiweb/clients/context";
import { IClientFactory } from "@multiweb/clients/IClientFactory";
import { LazyLoadingSectionLogger, LoggingSection } from "@multiweb/logging";
import { DIContainer } from "Global/Base/DIContainer";
import { Event as GlobalEvent } from "Global/Base/Events";
import { CancellationTokenSource } from "Global/Base/Promises/CancellationTokenSource";
import { StringUtils } from "Global/Base/Utils/StringUtils";
import { ModelDescriptionsManager } from "OW.Remoting/Remoting/Cache/ClassDescriptionCache/ModelDescriptionsManager";
import { FormControlParams } from "../../Controls/ControlInterfaces";
import { FrameContext } from "../../UIManagerInterfaces";
import { FormControl } from "../FormControl";
import { IMasterableBase, IMasterableFrame } from "./FrameInterfaces";

const logger = LazyLoadingSectionLogger.create(LoggingSection.autosave);

/**
 * Třída obsahující metody a property pro práci s transakčním kontextem
 * @includeToDoc
 */
export abstract class MasterableFormControl extends FormControl implements IMasterableBase {
    //#region Fields
    private _useMasterContext: boolean = false;

    private _contextClient: IContextClient;

    private _contextCreated: boolean = false;

    private _isContextCreatedByAction: boolean = false;

    private _handedContext: boolean = false;

    private _contextCancellationTokenSource = new CancellationTokenSource();
    private _objectName: string | undefined;
    private _supportsEditContext: Promise<boolean | null> = Promise.resolve(null);

    protected _masterFrame: IMasterableFrame;
    protected __transactionContextId: string = "";
    //#endregion Fields

    //#region Events
    public readonly contextCreated: GlobalEvent<MasterableFormControl, boolean> = new GlobalEvent<MasterableFormControl, boolean>();
    public readonly contextSaved: GlobalEvent<MasterableFormControl, boolean> = new GlobalEvent<MasterableFormControl, boolean>();
    public readonly contextRollbacked: GlobalEvent<MasterableFormControl, boolean> = new GlobalEvent<MasterableFormControl, boolean>();

    //#endregion Events

    //#region Properties
    public get masterFrame(): IMasterableFrame {
        return this._masterFrame;
    }

    public set masterFrame(value: IMasterableFrame) {
        this._masterFrame = value;
    }

    public get isEditContextInitiator(): boolean {
        return this._contextCreated;
    }

    public get hasHandedContext(): boolean {
        return this._handedContext;
    }

    public get useMasterContext(): boolean {
        return this._useMasterContext;
    }

    public set useMasterContext(value: boolean) {
        this._useMasterContext = value;
    }

    public get isContextCreatedByAction(): boolean {
        return this._isContextCreatedByAction;
    }

    public set isContextCreatedByAction(value: boolean) {
        this._isContextCreatedByAction = value;
    }

    public get transactionContextId(): string | undefined {
        if (this.useMasterContext) {
            if (this.masterFrame) {
                const masterFrameContextFunc = this.masterFrame?.frameContext?.getTransactionContextId;

                if (masterFrameContextFunc) {
                    const masterFrameTransactionId = masterFrameContextFunc();
                    logger.debug(`Frame id: ${this.element?.id} settings: UseMasterContext with MasterFrame. Using MasterFrame transactionId: ${masterFrameTransactionId}`);
                    return masterFrameTransactionId;
                } else {
                    logger.debug(`Frame id: ${this.element?.id} settings: UseMasterContext with MasterFrame, but there's not available transactionId in MasterFrame`);
                    return undefined;
                }
            } else {
                const ownerFormContextId = this._checkForOwnerFormContext();

                if (ownerFormContextId) {
                    logger.debug(`Frame id: ${this.element?.id} settings: UseMasterContext without MasterFrame. Using ownerForm transactionId: ${ownerFormContextId}`);
                    return ownerFormContextId;
                } else {
                    logger.debug(`Frame id: ${this.element?.id} settings: UseMasterContext without MasterFrame, but there's not available transactionId in OwnerForm`);
                    return undefined;
                }
            }
        } else {
            const ownerFormContextId = this._checkForOwnerFormContext();

            // Pokud mám na OwnerFormu Id, což může být předáno třeba skrze LOV/Akce tak ho využiji i bez UseMasterContext
            if (ownerFormContextId) {
                this._handedContext = true;
                logger.debug(`Frame id: ${this.element?.id} using transactionId: ${ownerFormContextId} handed over by OwnerForm`);
                return ownerFormContextId;
            }

            logger.debug(`Frame id: ${this.element?.id} settings: Using default frame transactionId: ${this.__transactionContextId}`);
            return this.__transactionContextId;
        }
    }

    public set transactionContextId(value: string) {
        this.__transactionContextId = value;
    }

    public get frameContext(): FrameContext {
        return {
            getTransactionContextId: () => this.transactionContextId,
            roles: this.ownerForm?.formContext?.roles,
            rightsSettingsVersion: this.ownerForm?.formContext?.rightsSettingsVersion,
        };
    }

    public get supportsEditContext(): Promise<boolean | null> {
        return this._supportsEditContext;
    }

    public get objectName(): string {
        return this._objectName;
    }
    public set objectName(value: string) {
        this._objectName = value;
        this._supportsEditContext = this._checkSupportsEditContext(value);
    }
    //#region Properties

    //#region Constructors
    constructor(params: FormControlParams) {
        super(params);
        this.useMasterContext = this.getBoolDataAttr("UseMasterContext");
        this._contextClient = DIContainer.instance.create(IClientFactory).get(ClientType.context);
    }
    //#endregion Constructors

    public async createContext(): Promise<void> {
        if (!(await this.supportsEditContext) || this._contextCreated || this._handedContext) {
            return;
        }

        if (this.useMasterContext) {
            await this.masterFrame?.createContext();
            return;
        }

        this._contextCreated = true;

        this.contextCreated.trigger(this, true);

        this.__transactionContextId = await this._contextClient.createContext(this._contextCancellationTokenSource.token);

        logger.debug(`Frame id: ${this.element?.id} created transactionId: ${this.__transactionContextId}`);
    }

    public async rollbackContext(): Promise<boolean | void> {
        if (!(await this.supportsEditContext))
            return;
        if (!this.__transactionContextId) {
            logger.debug(`Frame id: ${this.element?.id} requested rollback of context, but there's not available transactionId`);
            return;
        }

        if (!this.useMasterContext && !this._handedContext) {
            const storedContextId = this.__transactionContextId;
            this.__transactionContextId = undefined;
            const rollback = await this._contextClient.rollbackContext(storedContextId, this._contextCancellationTokenSource.token);

            if (rollback) {
                this._resetContextValues();
                logger.debug(`Frame id: ${this.element?.id} rollback of transactionId: ${this.__transactionContextId}`);

                this.contextRollbacked.trigger(this, rollback);
            } else {
                this.__transactionContextId = storedContextId;
                logger.debug(`Frame id: ${this.element?.id} rollback of transactionId: ${this.__transactionContextId} didn't go through correctly`);
            }

            return rollback;
        }
    }

    public async saveContext(): Promise<boolean | void> {
        if (!(await this.supportsEditContext))
            return;
        if (!this.__transactionContextId) {
            logger.debug(`Frame id: ${this.element?.id} requested context saving, but there's not available TransactionID`);
            return;
        }

        if (!this.useMasterContext && !this._handedContext) {
            const storedContextId = this.__transactionContextId;
            this.__transactionContextId = undefined;
            const saved = await this._contextClient.commitContext(storedContextId, this._contextCancellationTokenSource.token);

            if (saved) {
                this._resetContextValues();
                logger.debug(`Frame id: ${this.element?.id} saved transactionId: ${this.__transactionContextId}`);

                this.contextSaved.trigger(this, saved);
            } else {
                this.__transactionContextId = storedContextId;
                logger.debug(`Frame id: ${this.element?.id} saving transactionId: ${this.__transactionContextId} didn't go through correctly`);
            }

            return saved;
        }
    }

    private _resetContextValues(): void {
        this.__transactionContextId = undefined;
        this._contextCreated = false;
        this._handedContext = false;
        this._isContextCreatedByAction = false;
    }

    private _checkForOwnerFormContext(): string | void {
        const ownerFormContextFunc = this.ownerForm?.transactionContextGetter;

        if (ownerFormContextFunc) {
            return ownerFormContextFunc();
        }
    }
    /** Získá informaci o tom, jestli zadaná třída podporuje práci v editačním kontextu. */
    protected _checkSupportsEditContext(objectName: string): Promise<boolean> {
        try {
            if (StringUtils.isNullOrEmpty(objectName))
                return Promise.resolve(false);
            return ModelDescriptionsManager.instance.getClassInfo(objectName).then(i => i.supportsContext);
        } catch {
            throw new Error(`Unable to get requested class info for '${objectName}`);
        }
    }
}
